import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useMemo, useState, useRef } from "react";
import {
  determineChartContent,
  generateChartSegmentNamesForCSV,
  hasNoData,
} from "../../utils";
import {
  DetectionSegment,
  useProductDetectionsBySegment,
} from "./hooks/useProductDetectionsBySegment";
import { convertProductDetectionData } from "./utils";
import { ClusteredColumnChart } from "../../../../components/charts/ClusteredColumnChart/ClusteredColumnChart";
import { PeriodSelector } from "../../../../components/PeriodSelector";
import { ChartWrapper } from "../../../../components/ChartWrapper";
import useMatchProductsById from "./hooks/useMatchProductsById";
import { BarChartColors } from "../../../../components/charts/constants";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { CHART_CONSTANTS } from "../constants";
import { useCountryNameMap } from "../../../../hooks/useCountryNameMap";

const { baseId, chartTitle, tooltip } =
  CHART_CONSTANTS.keyPlayerProductDetectionsBySegment;
interface Props {
  isLoading: boolean;
  topProductIds: number[];
}

export function KeyPlayerProductDetectionsBySegmentChart(props: Props) {
  const chartRef = useRef<am4charts.XYChart>(null);
  const [selectedPeriod, setSelectedPeriod] = useState("1");
  const [selectedSegment, setSelectedSegment] = useState(
    DetectionSegment.COUNTRY
  );
  const { countryNameMap } = useCountryNameMap();
  const combinedFilters = useCombinedFilters();
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const {
    isLoading: isDetectionDataLoading,
    productDetectionsBySegmentBreakdown,
  } = useProductDetectionsBySegment({
    productIds: props.topProductIds,
    companyFilters: combinedFilters || {},
    localizeInstalls,
    lookBackYears: Number.parseInt(selectedPeriod),
    segment: selectedSegment,
  });

  const { productsNameMapById } = useMatchProductsById(
    props.topProductIds ?? []
  );

  const chartData = useMemo(
    () =>
      convertProductDetectionData({
        productDetectionData: productDetectionsBySegmentBreakdown,
        productNameInfo: productsNameMapById,
        segment: selectedSegment,
        countryNameMap,
      }),
    [
      productDetectionsBySegmentBreakdown,
      productsNameMapById,
      selectedSegment,
      countryNameMap,
    ]
  );

  const isLoading = isDetectionDataLoading || props.isLoading;

  const noDataReturned = useMemo(
    () => hasNoData(isLoading, chartData),
    [chartData, isLoading]
  );

  const chartContent = useMemo(() => {
    return determineChartContent({
      isLoading,
      noDataReturned,
      baseId,
      chartToRender: (
        <ClusteredColumnChart
          id={`${baseId}-chart`}
          chartData={chartData}
          baseColor={BarChartColors}
          yAxisLabel="New Product Detections"
          ref={chartRef}
        />
      ),
    });
  }, [isLoading, noDataReturned, chartData]);

  function handlePeriodSelect(period: string) {
    setSelectedPeriod(period);
  }

  const csvFieldMappings = useMemo(() => {
    const fieldMappings: Record<string, string> = {
      category: generateChartSegmentNamesForCSV(selectedSegment),
    };

    if (chartData.length > 0) {
      const uniqueProducts = new Set<string>();
      chartData.forEach((item) => {
        item.cluster.forEach((product) => {
          uniqueProducts.add(product.name);
        });
      });

      uniqueProducts.forEach((productName) => {
        fieldMappings[productName] = `${productName} (New Product Detections)`;
      });
    }

    return fieldMappings;
  }, [chartData, selectedSegment]);

  function handleSegmentSelect(event: SelectChangeEvent) {
    setSelectedSegment(event.target.value as DetectionSegment);
  }

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      chartRef={chartRef}
      noData={noDataReturned}
      isLoading={isLoading}
      titleExtras={
        <Box
          display="flex"
          flexDirection="row"
          gap="5px"
          flexWrap="wrap"
          minWidth={0}
        >
          <PeriodSelector
            id={`${baseId}-period-selector`}
            selectedPeriod={selectedPeriod}
            onChange={handlePeriodSelect}
            maxYears={5}
          />
          <FormControl size="small" sx={{ minWidth: 180, maxWidth: "100%" }}>
            <Select
              id={`${baseId}-segment-select`}
              value={selectedSegment}
              onChange={handleSegmentSelect}
            >
              <MenuItem value={`${DetectionSegment.COUNTRY}`}>
                Segment: Country
              </MenuItem>
              <MenuItem value={`${DetectionSegment.REGION}`}>
                Segment: Region
              </MenuItem>
              <MenuItem value={`${DetectionSegment.INDUSTRY}`}>
                Segment: HG Industry
              </MenuItem>
              <MenuItem value={`${DetectionSegment.INDUSTRY_NAICS}`}>
                Segment: NAICS
              </MenuItem>
              <MenuItem value={`${DetectionSegment.EMPLOYEE}`}>
                Segment: Employee Range
              </MenuItem>
              <MenuItem value={`${DetectionSegment.REVENUE}`}>
                Segment: Revenue Range
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      }
      csvFieldMappings={csvFieldMappings}
    />
  );
}
